import { ArrowLeft, ChevronLeft, ChevronRight, Copy, Check } from "lucide-react";
import { useState } from "react";
import type { StoryItem, Collection } from "../data/types";
import { StatChart } from "./StatChart";
import { StoryPlayer } from "./StoryPlayer";

function TikTokCaption({ title, description }: { title: string; description: string }) {
  const [copied, setCopied] = useState<"title" | "desc" | null>(null);
  const copy = (text: string, which: "title" | "desc") => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  };
  return (
    <div style={{ padding: "20px 20px 0" }}>
      <h2 style={{ fontFamily: "var(--font-heading)", color: "rgba(255,255,255,0.9)", fontWeight: 700, fontSize: 16, margin: "0 0 12px" }}>
        TikTok Caption
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ backgroundColor: "rgba(255,255,255,0.06)", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 15, fontWeight: 600, color: "rgba(255,255,255,0.85)", margin: 0, lineHeight: 1.5, flex: 1 }}>
              {title}
            </p>
            <button onClick={() => copy(title, "title")} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0 }}>
              {copied === "title" ? <Check size={14} color="#4ade80" /> : <Copy size={14} color="rgba(255,255,255,0.4)" />}
            </button>
          </div>
        </div>
        <div style={{ backgroundColor: "rgba(255,255,255,0.06)", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "rgba(255,255,255,0.65)", margin: 0, lineHeight: 1.6, flex: 1, whiteSpace: "pre-line" }}>
              {description}
            </p>
            <button onClick={() => copy(description, "desc")} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0 }}>
              {copied === "desc" ? <Check size={14} color="#4ade80" /> : <Copy size={14} color="rgba(255,255,255,0.4)" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DetailPage({
  collection,
  item,
  onBack,
  onNavigate,
}: {
  collection: Collection;
  item: StoryItem;
  onBack: () => void;
  onNavigate: (col: Collection, item: StoryItem) => void;
}) {
  const ordered = collection.categories.flatMap((cat) =>
    collection.items.filter((i) => i.category === cat.name)
  );
  const currentIndex = ordered.indexOf(item);
  const prev = currentIndex > 0 ? ordered[currentIndex - 1] : null;
  const next = currentIndex < ordered.length - 1 ? ordered[currentIndex + 1] : null;

  const bgColor = collection.bgColor;

  return (
    <div
      className="min-h-screen"
      style={{
        display: "flex",
        flexDirection: "column",
        background: bgColor,
      }}
    >
      {/* Floating back button */}
      <button
        onClick={onBack}
        style={{
          position: "fixed",
          top: 16,
          left: 16,
          zIndex: 60,
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.2)",
          backgroundColor: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ArrowLeft size={20} color="white" />
      </button>

      <main className="max-w-xl mx-auto pb-6" style={{ flex: 1, width: "100%" }}>
        <StoryPlayer slides={item.story} emoji={item.emoji} capturePrefix={item.id} bgColor={bgColor} showSwipeHint />

        {item.tiktok && <TikTokCaption title={item.tiktok.title} description={item.tiktok.description} />}

        {/* Statistical fallacy interactive elements */}
        {item.category === "Manipulasi Statistik" && (
          <div style={{ padding: 20 }}>
            <StatChart fallacyId={item.id} />
          </div>
        )}

        {/* Related Items */}
        {item.related.length > 0 && (
          <div className="mt-6" style={{ padding: 20 }}>
            <h2
              style={{ fontFamily: "var(--font-heading)", color: "rgba(255,255,255,0.9)" }}
              className="font-bold text-base mb-4"
            >
              Terkait
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {item.related
                .map((id) => collection.items.find((i) => i.id === id))
                .filter(Boolean)
                .map((rel) => (
                  <button
                    key={rel!.id}
                    onClick={() => onNavigate(collection, rel!)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 16px",
                      backgroundColor: "rgba(255,255,255,0.95)",
                      border: "none",
                      borderRadius: 12,
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{rel!.emoji}</span>
                    <div>
                      <p style={{ fontFamily: "var(--font-heading)", fontSize: 17, fontWeight: 600, color: "#111827", margin: 0 }}>
                        {rel!.name}
                      </p>
                      <p style={{ fontFamily: "var(--font-body)", fontSize: 16, color: "#6b7280", margin: 0, lineHeight: 1.7, letterSpacing: "-0.01em", marginTop: 2 }}>
                        {rel!.tldr}
                      </p>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-8 mb-6 text-center" style={{ padding: 20 }}>
          <button
            onClick={onBack}
            className="text-base font-medium hover:underline"
            style={{ fontFamily: "var(--font-body)", color: "rgba(255,255,255,0.6)", backgroundColor: "transparent", border: "none", cursor: "pointer" }}
          >
            Kembali ke daftar
          </button>
        </footer>
      </main>

      {/* Sticky bottom navigation bar */}
      <nav
        style={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgba(2,26,25,0.85)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          zIndex: 50,
        }}
      >
        <div
          style={{
            maxWidth: 576,
            margin: "0 auto",
            display: "flex",
            height: 48,
          }}
        >
          {prev && (
            <button
              onClick={() => onNavigate(collection, prev)}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "0 16px",
                fontFamily: "var(--font-body)",
                fontSize: 15,
                color: "rgba(255,255,255,0.7)",
                backgroundColor: "transparent",
                border: "none",
                borderRight: next ? "1px solid rgba(255,255,255,0.1)" : "none",
                cursor: "pointer",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              <ChevronLeft size={16} style={{ flexShrink: 0, color: "rgba(255,255,255,0.4)" }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{prev.emoji} {prev.name}</span>
            </button>
          )}
          {next && (
            <button
              onClick={() => onNavigate(collection, next)}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 6,
                padding: "0 16px",
                fontFamily: "var(--font-body)",
                fontSize: 15,
                color: "rgba(255,255,255,0.7)",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{next.emoji} {next.name}</span>
              <ChevronRight size={16} style={{ flexShrink: 0, color: "rgba(255,255,255,0.4)" }} />
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}
